import React, { useEffect, useRef, useReducer, useState } from 'react'
import PropTypes from 'prop-types'
import debounce from 'lodash/debounce'
import onClickOutside from 'react-onclickoutside'

import cn from '../../libs/class-name'

import Icon from '../Icon'
import Logo from '../Logo'

import chevronDown from '../../assets/icons/chevron-down.svg'

const useNavBar = currentHref => {
  const reducer = (
    state,
    { currentEl = state.currentEl, activeEl = state.activeEl }
  ) => {
    const el = currentEl || activeEl

    if (!el) {
      return { x: 0, width: 0, currentEl, activeEl }
    }

    return { x: el.offsetLeft, width: el.offsetWidth, currentEl, activeEl }
  }

  const linksRef = useRef(null)

  const [state, dispatch] = useReducer(reducer, {
    x: 0,
    width: 0,
    currentEl: null,
    activeEl: null
  })

  useEffect(() => {
    if (linksRef.current) {
      dispatch({
        activeEl: linksRef.current.querySelector('.NavLink--active')
      })
    }
  }, [currentHref])

  useEffect(() => {
    const update = () => dispatch({})
    const debouncedUpdate = debounce(update, 300)

    if (linksRef.current) {
      dispatch({ activeEl: linksRef.current.querySelector('.NavLink--active') })
    }

    global.addEventListener('resize', debouncedUpdate, false)
    global.addEventListener('orientationchange', update, false)
    global.addEventListener('ready', update, false)

    return () => {
      debouncedUpdate.cancel()

      global.removeEventListener('resize', debouncedUpdate, false)
      global.removeEventListener('orientationchange', update, false)
      global.removeEventListener('ready', update, false)
    }
  }, [])

  const setCurrentElement = el => dispatch({ currentEl: el })

  return [state, setCurrentElement, linksRef]
}

// onClickOutside requires the component to have a prototype, which arrow functions do not have
const Header = function Header({
  classNames,
  siteTitle,
  children,
  action,
  currentHref,
  menuLabel
}) {
  const hasChildren = React.Children.count(children) > 0
  const [navIsOpen, setNavIsOpen] = useState(false)
  const [bar, setCurrentElement, linksRef] = useNavBar(currentHref)
  Header.handleClickOutside = () => setNavIsOpen(false)

  const renderLinks = (menu = false) =>
    React.Children.map(children, link => (
      <div
        className="Header__link"
        onMouseEnter={event => {
          setCurrentElement(event.currentTarget.firstChild)
        }}
        onMouseLeave={() => {
          setCurrentElement(null)
        }}
      >
        {React.cloneElement(link, {
          size: menu ? 'l' : 'default',
          invert: !menu,
          onFocus: event => {
            setCurrentElement(event.currentTarget)
          },
          onBlur: () => {
            setCurrentElement(null)
          }
        })}
      </div>
    ))

  return (
    <header className={cn('Header', { navIsOpen }, classNames)}>
      <Logo alt={siteTitle} invert size="l" />
      {hasChildren && (
        <>
          <div className="Header__links" ref={linksRef}>
            <div
              className="Header__bar"
              aria-hidden
              style={{
                width: `${bar.width}px`,
                transform: `translateX(${bar.x}px)`
              }}
            />
            {renderLinks()}
            {action && <div className="Header__action">{action}</div>}
          </div>
          <div className="Header__menu">
            <button
              type="button"
              className="Header__menuButton"
              onClick={() => setNavIsOpen(!navIsOpen)}
            >
              {menuLabel}
              <Icon source={chevronDown} />
            </button>
            <div className="Header__menuLinks">
              {renderLinks(true)}
              {action && <div className="Header__action">{action}</div>}
            </div>
          </div>
        </>
      )}
    </header>
  )
}

Header.propTypes = {
  classNames: PropTypes.arrayOf(PropTypes.string),
  siteTitle: PropTypes.string.isRequired,
  children: PropTypes.node,
  menuLabel: PropTypes.string.isRequired,
  action: PropTypes.node,
  currentHref: PropTypes.string
}

const clickOutsideConfig = {
  handleClickOutside: () => Header.handleClickOutside
}

export default onClickOutside(Header, clickOutsideConfig)
